import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Sparkles, 
  ArrowRight, 
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Info,
  Globe,
  Zap,
  FileText,
  Link2,
  Gauge,
  ChevronDown,
  ChevronUp,
  Download,
  Loader2,
  Building2,
  Mail,
  Shield,
  Smartphone,
  Clock,
  Image,
  Type,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { api } from '../context/AuthContext';
import { toast } from 'sonner';

const INDUSTRIES = [
  'Technology', 'E-commerce', 'Healthcare', 'Finance', 'Real Estate',
  'Education', 'Manufacturing', 'Professional Services', 'Retail', 'Other'
];

const SEVERITY_CONFIG = {
  high: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: 'High Priority' },
  medium: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle, label: 'Medium Priority' },
  low: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Info, label: 'Low Priority' },
  info: { color: 'bg-slate-100 text-slate-700 border-slate-200', icon: Info, label: 'Info' }
};

const SCORE_CONFIG = {
  excellent: { min: 80, color: 'text-emerald-600', bg: 'bg-emerald-500', label: 'Excellent' },
  good: { min: 60, color: 'text-blue-600', bg: 'bg-blue-500', label: 'Good' },
  needsWork: { min: 40, color: 'text-amber-600', bg: 'bg-amber-500', label: 'Needs Work' },
  poor: { min: 0, color: 'text-red-600', bg: 'bg-red-500', label: 'Poor' }
};

const getScoreConfig = (score) => {
  if (score >= 80) return SCORE_CONFIG.excellent;
  if (score >= 60) return SCORE_CONFIG.good;
  if (score >= 40) return SCORE_CONFIG.needsWork;
  return SCORE_CONFIG.poor;
};

const ScoreCircle = ({ score, size = 'lg', label }) => {
  const config = getScoreConfig(score);
  const sizeClasses = {
    sm: 'w-16 h-16 text-xl',
    md: 'w-24 h-24 text-3xl',
    lg: 'w-32 h-32 text-4xl'
  };
  const strokeWidth = size === 'lg' ? 8 : 6;
  const radius = size === 'lg' ? 56 : size === 'md' ? 40 : 28;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className={`relative ${sizeClasses[size]} flex items-center justify-center`}>
        <svg className="w-full h-full transform -rotate-90">
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            stroke="#e2e8f0"
            strokeWidth={strokeWidth}
            fill="none"
          />
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={circumference - progress}
            strokeLinecap="round"
            className={config.color}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`font-bold font-heading ${config.color}`}>{score}</span>
          {size !== 'sm' && <span className="text-xs text-slate-500">/100</span>}
        </div>
      </div>
      {label && <span className="mt-2 text-sm font-medium text-slate-600">{label}</span>}
    </div>
  );
};

const CategoryCard = ({ title, score, icon: Icon, details, issues }) => {
  const config = getScoreConfig(score);
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="gds-card overflow-hidden">
      <CardContent className="p-0">
        <div 
          className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${config.bg}/10 flex items-center justify-center`}>
                <Icon className={`w-5 h-5 ${config.color}`} />
              </div>
              <div>
                <h3 className="font-semibold">{title}</h3>
                <p className="text-sm text-slate-500">{issues?.length || 0} issues found</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <ScoreCircle score={score} size="sm" />
              {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </div>
          </div>
        </div>
        
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-slate-100"
            >
              <div className="p-4 space-y-4">
                {/* Quick Stats */}
                {details && Object.keys(details).length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(details).slice(0, 6).map(([key, value]) => (
                      <div key={key} className="p-2 rounded-lg bg-slate-50">
                        <p className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</p>
                        <p className="font-medium text-sm truncate">{String(value)}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Issues */}
                {issues && issues.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">Issues Found:</p>
                    {issues.map((issue, idx) => {
                      const severityConfig = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.info;
                      return (
                        <div key={idx} className={`p-3 rounded-lg border ${severityConfig.color}`}>
                          <div className="flex items-start gap-2">
                            <severityConfig.icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-sm">{issue.title}</p>
                              <p className="text-xs mt-1 opacity-80">{issue.description}</p>
                              {issue.fix && (
                                <p className="text-xs mt-2 font-medium">Fix: {issue.fix}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
};

export default function FreeAuditPage() {
  const [loading, setLoading] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [formData, setFormData] = useState({
    website: '',
    email: '',
    business_name: '',
    industry: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.website) {
      toast.error('Please enter a website URL');
      return;
    }
    if (!formData.email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    setAuditResult(null);
    
    try {
      const response = await api.post('/audit/free', formData);
      setAuditResult(response.data);
      toast.success('Audit complete!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run audit. Please check the URL.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl">GDS</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="gds-btn-primary">Get Started</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {!auditResult ? (
          // Audit Form
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto"
          >
            <div className="text-center mb-10">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center mx-auto mb-6 shadow-xl shadow-indigo-500/30">
                <Search className="w-10 h-10 text-white" />
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Free SEO Audit Report
              </h1>
              <p className="text-lg text-slate-600 max-w-xl mx-auto">
                Get a comprehensive analysis of your website's SEO health in under 30 seconds. 
                Real data, actionable insights.
              </p>
            </div>

            <Card className="gds-card shadow-xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="website" className="text-base">Website URL <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="website"
                        type="text"
                        placeholder="https://example.com"
                        className="pl-10 h-12 text-base"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="business_name">Business Name</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="business_name"
                          type="text"
                          placeholder="Acme Inc."
                          className="pl-10"
                          value={formData.business_name}
                          onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="industry">Industry</Label>
                      <Select value={formData.industry} onValueChange={(v) => setFormData({ ...formData, industry: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {INDUSTRIES.map((ind) => (
                            <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-base">Email Address <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@company.com"
                        className="pl-10 h-12 text-base"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                    <p className="text-xs text-slate-500">Used to save your report and send you updates</p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-14 text-lg gds-btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Analyzing Website...
                      </>
                    ) : (
                      <>
                        <Search className="w-5 h-5 mr-2" />
                        Run Free SEO Audit
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t border-slate-200">
                  <p className="text-center text-sm text-slate-500">
                    <Shield className="w-4 h-4 inline mr-1" />
                    Your data is secure. We never share your information.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* What We Check */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { icon: Shield, label: 'Technical SEO' },
                { icon: FileText, label: 'On-Page SEO' },
                { icon: Type, label: 'Content Quality' },
                { icon: Link2, label: 'Backlinks' },
                { icon: Gauge, label: 'Performance' }
              ].map((item) => (
                <div key={item.label} className="text-center p-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mx-auto mb-2">
                    <item.icon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <p className="text-sm font-medium text-slate-700">{item.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        ) : (
          // Audit Results
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            {/* Results Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2">
                  SEO Audit Report
                </h1>
                <p className="text-slate-600 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  {auditResult.website}
                  <Badge variant="outline" className="ml-2">
                    <Clock className="w-3 h-3 mr-1" />
                    {auditResult.crawl_time}s
                  </Badge>
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={() => setAuditResult(null)}>
                  <Search className="w-4 h-4 mr-2" />
                  New Audit
                </Button>
                <Link to="/register">
                  <Button className="gds-btn-primary">
                    Get Full Report
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Overall Score Card */}
            <Card className="gds-card overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-violet-600 p-8 text-white">
                <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                  <div className="text-center md:text-left">
                    <h2 className="text-2xl font-bold font-heading mb-2">Overall SEO Score</h2>
                    <p className="text-indigo-100 max-w-md">
                      {auditResult.overall_score >= 70 
                        ? "Your website has a solid SEO foundation. Focus on the issues below to improve further."
                        : auditResult.overall_score >= 50
                        ? "Your website needs some SEO improvements. Address the high-priority issues first."
                        : "Your website has significant SEO issues that need immediate attention."
                      }
                    </p>
                  </div>
                  <div className="relative">
                    <div className="w-40 h-40 rounded-full bg-white/20 flex items-center justify-center">
                      <div className="w-32 h-32 rounded-full bg-white flex items-center justify-center">
                        <div className="text-center">
                          <span className={`text-5xl font-bold ${getScoreConfig(auditResult.overall_score).color}`}>
                            {auditResult.overall_score}
                          </span>
                          <p className="text-sm text-slate-500">/100</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Issues Summary */}
              <CardContent className="p-6">
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 text-red-700">
                    <XCircle className="w-4 h-4" />
                    <span className="font-semibold">{auditResult.issues_count?.high || 0}</span>
                    <span className="text-sm">High Priority</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 text-amber-700">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="font-semibold">{auditResult.issues_count?.medium || 0}</span>
                    <span className="text-sm">Medium Priority</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-700">
                    <Info className="w-4 h-4" />
                    <span className="font-semibold">{auditResult.issues_count?.low || 0}</span>
                    <span className="text-sm">Low Priority</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Score Breakdown */}
            <div className="grid md:grid-cols-5 gap-4">
              {[
                { key: 'technical', label: 'Technical', icon: Shield },
                { key: 'onpage', label: 'On-Page', icon: FileText },
                { key: 'content', label: 'Content', icon: Type },
                { key: 'backlinks', label: 'Backlinks', icon: Link2 },
                { key: 'performance', label: 'Performance', icon: Gauge }
              ].map((cat) => (
                <Card key={cat.key} className="gds-card text-center p-6">
                  <div className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${getScoreConfig(auditResult.scores[cat.key]).bg}/10`}>
                    <cat.icon className={`w-6 h-6 ${getScoreConfig(auditResult.scores[cat.key]).color}`} />
                  </div>
                  <p className={`text-3xl font-bold ${getScoreConfig(auditResult.scores[cat.key]).color}`}>
                    {auditResult.scores[cat.key]}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">{cat.label}</p>
                </Card>
              ))}
            </div>

            {/* Detailed Analysis */}
            <div className="space-y-4">
              <h2 className="font-heading text-xl font-bold">Detailed Analysis</h2>
              
              <CategoryCard 
                title="Technical SEO" 
                score={auditResult.scores.technical}
                icon={Shield}
                details={{
                  'HTTPS Secure': auditResult.technical?.https ? 'Yes' : 'No',
                  'Mobile Friendly': auditResult.technical?.mobile_friendly ? 'Yes' : 'No',
                  'Robots.txt': auditResult.technical?.robots_txt ? 'Found' : 'Missing',
                  'Sitemap': auditResult.technical?.sitemap ? 'Found' : 'Missing',
                  'Canonical': auditResult.technical?.canonical_tag ? 'Present' : 'Missing',
                  'Schema.org': auditResult.technical?.structured_data ? 'Found' : 'Missing'
                }}
                issues={auditResult.technical?.issues}
              />
              
              <CategoryCard 
                title="On-Page SEO" 
                score={auditResult.scores.onpage}
                icon={FileText}
                details={{
                  'Title Length': auditResult.onpage?.details?.title_length ? `${auditResult.onpage.details.title_length} chars` : 'N/A',
                  'Meta Description': auditResult.onpage?.details?.meta_desc_length ? `${auditResult.onpage.details.meta_desc_length} chars` : 'Missing',
                  'H1 Tags': auditResult.onpage?.h1_count || 0,
                  'H2 Tags': auditResult.onpage?.h2_count || 0,
                  'Images': auditResult.onpage?.image_count || 0,
                  'Word Count': auditResult.onpage?.word_count || 0
                }}
                issues={auditResult.onpage?.issues}
              />
              
              <CategoryCard 
                title="Content Quality" 
                score={auditResult.scores.content}
                icon={Type}
                details={{
                  'Content Length': auditResult.content?.content_length || 'Unknown',
                  'Internal Links': auditResult.onpage?.internal_links || 0,
                  'External Links': auditResult.onpage?.external_links || 0
                }}
                issues={auditResult.content?.issues}
              />
              
              <CategoryCard 
                title="Backlink Profile" 
                score={auditResult.scores.backlinks}
                icon={Link2}
                details={{
                  'Outbound Links': auditResult.backlinks?.external_links_found || 0,
                  'Social Profiles': auditResult.backlinks?.social_profiles?.length || 0
                }}
                issues={auditResult.backlinks?.issues}
              />
              
              <CategoryCard 
                title="Performance" 
                score={auditResult.scores.performance}
                icon={Gauge}
                details={{
                  'Load Time': auditResult.performance?.load_time || 'N/A',
                  'Page Size': auditResult.performance?.page_size || 'N/A'
                }}
                issues={auditResult.performance?.issues}
              />
            </div>

            {/* Recommendations */}
            {auditResult.recommendations && auditResult.recommendations.length > 0 && (
              <Card className="gds-card">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-indigo-500" />
                    Top Recommendations
                  </CardTitle>
                  <CardDescription>Prioritized actions to improve your SEO</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {auditResult.recommendations.map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-4 rounded-xl bg-slate-50">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          rec.priority === 'critical' ? 'bg-red-100 text-red-600' :
                          rec.priority === 'high' ? 'bg-amber-100 text-amber-600' :
                          'bg-blue-100 text-blue-600'
                        }`}>
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-medium">{rec.title}</p>
                          <p className="text-sm text-slate-600 mt-1">
                            Impact: {rec.impact} • Effort: {rec.effort}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CTA */}
            <Card className="gds-card bg-gradient-to-r from-indigo-50 to-violet-50 border-indigo-200">
              <CardContent className="p-8 text-center">
                <h3 className="font-heading text-2xl font-bold mb-3">
                  Ready to Fix These Issues?
                </h3>
                <p className="text-slate-600 mb-6 max-w-xl mx-auto">
                  Get a dedicated team to implement these fixes and grow your organic traffic. 
                  Plans start at $799/month.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/register">
                    <Button size="lg" className="gds-btn-primary w-full sm:w-auto">
                      Start Your Growth Journey
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <Button size="lg" variant="outline" onClick={() => window.print()}>
                    <Download className="w-5 h-5 mr-2" />
                    Download Report
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>
    </div>
  );
}
